import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useContext, useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, ErrorNotice, Spinner } from '@crm/ui';
import { ApiError } from '../api/client.ts';
import { isConflict } from '../api/write.ts';
import { errorMessage } from '../lib/format.ts';
import { TenantContext } from '../tenant/TenantProvider.tsx';

/**
 * Erreur d'une écriture : la raison du serveur (`detail`) EN PREMIER ; sur 412, le message de conflit.
 * Les erreurs de champs déjà affichées à côté de leur champ (`boundFields`) ne sont pas répétées.
 */
export function FormError({ error, boundFields = [] }: { error: unknown; boundFields?: string[] }) {
  const { t } = useTranslation();
  if (!error) return null;
  const problem = error instanceof ApiError ? error.problem : null;
  const conflict = isConflict(error);
  const unbound = problem?.errors?.filter((e) => !boundFields.some((f) => e.field === f || e.field === `/${f}` || e.field.endsWith(`.${f}`)));
  return (
    <ErrorNotice
      title={conflict ? (problem?.detail ?? '') : (problem?.title ?? t('errors.title'))}
      detail={conflict ? t('admin.common.conflict') : errorMessage(error)}
      errors={unbound}
      requestId={problem?.request_id}
    />
  );
}

/** Erreur de lecture (liste, fiche) : même règle, la raison du serveur d'abord. */
export function LoadError({ error }: { error: unknown }) {
  const { t } = useTranslation();
  if (!error) return null;
  const problem = error instanceof ApiError ? error.problem : null;
  return <ErrorNotice title={problem?.title ?? t('errors.title')} detail={errorMessage(error)} requestId={problem?.request_id} />;
}

export function Loading() {
  const { t } = useTranslation();
  return (
    <div className="p-5">
      <Spinner label={t('app.loading')} />
    </div>
  );
}

/**
 * Mutation d'administration : invalide les requêtes touchées après succès, et AUSSI après un 412
 * (quelqu'un a modifié l'objet entre-temps : on recharge la version à jour).
 */
export function useWrite<TVars, TRes>(
  fn: (vars: TVars) => Promise<TRes>,
  opts: { invalidate: QueryKey[]; onSuccess?: (res: TRes, vars: TVars) => void },
) {
  const qc = useQueryClient();
  const tenant = useContext(TenantContext);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const refresh = () => Promise.all(opts.invalidate.map((queryKey) => qc.invalidateQueries({ queryKey })));
  return useMutation({
    mutationFn: fn,
    onSuccess: async (res, vars) => {
      // Le rafraîchissement peut remplacer lui-même le formulaire après une
      // transition : conserver alors sa callback, tout en ignorant une réponse
      // arrivée après l'abandon ou une navigation pendant le rafraîchissement.
      const activeAtResponse = mounted.current;
      const url = window.location.href;
      // switchTenant retire ce cache même si le chemin reste identique.
      const contextKey = ['context', tenant?.tenantId];
      const contextQuery = qc.getQueryCache().find({ queryKey: contextKey, exact: true });
      await refresh();
      const sameContext = !contextQuery || qc.getQueryCache().find({ queryKey: contextKey, exact: true }) === contextQuery;
      if (activeAtResponse && sameContext && window.location.href === url) opts.onSuccess?.(res, vars);
    },
    onError: (err) => {
      if (isConflict(err)) void refresh();
    },
  });
}

export function EmptyRow({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-6 text-center text-sm text-slate-500">{children}</td>
    </tr>
  );
}

export function StatusBadge({ status }: { status: 'INVITED' | 'ACTIVE' | 'SUSPENDED' }) {
  const { t } = useTranslation();
  const tone = status === 'ACTIVE' ? 'success' : status === 'SUSPENDED' ? 'danger' : 'warning';
  return <Badge tone={tone}>{t(`admin.users.status.${status}`)}</Badge>;
}

/** Récupère toutes les pages d'une liste à curseur (bornée, pour les listes de référence). */
export async function fetchAll<T>(page: (cursor: string | undefined) => Promise<{ items: T[]; next_cursor: string | null }>, maxPages = 25): Promise<T[]> {
  const all: T[] = [];
  let cursor: string | undefined;
  for (let i = 0; i < maxPages; i++) {
    const res = await page(cursor);
    all.push(...res.items);
    if (!res.next_cursor) break;
    cursor = res.next_cursor;
  }
  return all;
}
